/**
 * RoleBudgetEditDialog
 * Dialog for editing a role's monthly budget limit.
 */
import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useUpdateRoleBudget } from '@/hooks/useBudgetAdmin'
import type { BudgetRoleInfo } from '@/types/budgetAdmin'
import { formatBudgetUsd } from '@/types/budgetAdmin'

interface RoleBudgetEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  role: BudgetRoleInfo
}

export function RoleBudgetEditDialog({
  open,
  onOpenChange,
  role,
}: RoleBudgetEditDialogProps) {
  const [isUnlimited, setIsUnlimited] = useState(role.monthlyBudgetUsd === null)
  const [budgetValue, setBudgetValue] = useState(
    role.monthlyBudgetUsd !== null ? String(role.monthlyBudgetUsd) : ''
  )
  const [error, setError] = useState<string | null>(null)

  const updateBudget = useUpdateRoleBudget()

  const handleSave = async () => {
    setError(null)

    let monthlyBudgetUsd: number | null = null
    if (!isUnlimited) {
      const budgetNum = parseFloat(budgetValue)
      if (isNaN(budgetNum) || budgetNum < 0) {
        setError('Please enter a valid budget amount (0 or greater)')
        return
      }
      monthlyBudgetUsd = budgetNum
    }

    try {
      await updateBudget.mutateAsync({
        roleId: role.id,
        request: { monthlyBudgetUsd },
      })
      onOpenChange(false)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update role budget'
      )
    }
  }

  const isPending = updateBudget.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Role Budget</DialogTitle>
          <DialogDescription>
            Update the monthly budget limit for the {role.displayName} role.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Role Info */}
          <div className="text-sm text-muted-foreground">
            <p>
              <strong>Role:</strong> {role.displayName}
            </p>
            <p>
              <strong>Current Limit:</strong>{' '}
              {formatBudgetUsd(role.monthlyBudgetUsd)}
            </p>
          </div>

          {/* Unlimited Toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="unlimited">Unlimited Budget</Label>
            <Switch
              id="unlimited"
              checked={isUnlimited}
              onCheckedChange={setIsUnlimited}
              disabled={isPending}
            />
          </div>

          {/* Budget Input (only shown when not unlimited) */}
          {!isUnlimited && (
            <div className="space-y-2">
              <Label htmlFor="budget">Monthly Budget (USD)</Label>
              <Input
                id="budget"
                type="number"
                min="0"
                step="1"
                placeholder="e.g., 100"
                value={budgetValue}
                onChange={(e) => setBudgetValue(e.target.value)}
                disabled={isPending}
              />
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isPending || (!isUnlimited && !budgetValue)}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
